import type { TranslationKeys } from './types';

export const fr: TranslationKeys = {
    // Navbar
    navbar: {
      schools: 'Écoles',
      howItWorks: 'Comment ça marche',
      aboutMe: 'À propos',
      pricing: 'Tarifs',
      faqs: 'FAQ',
      memories: 'Souvenirs',
      myAccount: 'Mon compte',
    },
    // Home Page - HERO MAIN (H1, tagline, subtitle)
    home: {
      title: 'Trouvez vos photos de surf en quelques secondes',
      tagline: 'Photographie de surf à Lanzarote',
      subtitle: 'Téléversez une photo et retrouvez vos souvenirs instantanément, sans parcourir des centaines d\'images',
      cta: 'Trouver mes photos',
      stats: {
        photos: 'Photos',
        schools: 'Écoles',
        surfers: 'Surfeurs satisfaits',
      },
    },
    // Schools Page - SURF SCHOOLS
    schools: {
      title: 'Écoles de surf',
      subtitle: 'Sélectionnez votre école de surf pour trouver vos photos',
      viewPhotos: 'Voir les photos',
      introTitle: 'Votre école, votre jour, vos souvenirs',
      descriptionMain: 'Sélectionnez l\'école où vous avez pris votre cours et retrouvez votre session organisée par date.',
      descriptionSecondary: 'Sessions quotidiennes de photographie de surf à Famara et La Santa.',
      loading: 'Chargement des écoles...',
      apiUnavailable:
        'Impossible de joindre le serveur (délai dépassé ou erreur). Affichage de la liste par défaut — réessayez dans quelques minutes.',
      retry: 'Réessayer',
      otherSchools: 'AUTRES ÉCOLES',
      valueHighQuality: 'Photos haute qualité',
      valueInstantDownload: 'Téléchargement instantané',
      valueSecurePayment: 'Paiement sécurisé',
    },
    // How It Works Page - PHOTO ACCESS (SELFIE)
    howItWorks: {
      title: 'Trouvez vos photos en toute sécurité',
      subtitle: 'Téléversez un selfie pour que notre système identifie automatiquement uniquement vos photos.',
      step1: {
        title: 'Choisissez votre école de surf',
        description: 'Sélectionnez l\'école où vous avez pris votre cours',
      },
      step2: {
        title: 'Sélectionnez la date de votre session',
        description: 'Choisissez le jour où vous avez surfé',
      },
      step3: {
        title: 'Téléversez un selfie',
        description: 'Notre système identifiera automatiquement uniquement vos photos',
      },
      step4: {
        title: 'Consultez vos photos',
        description: 'Voyez toutes les photos sur lesquelles vous apparaissez',
      },
      step5: {
        title: 'Ajoutez vos souvenirs',
        description: 'Sélectionnez les photos que vous souhaitez conserver',
      },
      step6: {
        title: 'Payez en toute sécurité',
        description: 'Effectuez le paiement rapidement et en toute sécurité',
      },
      step7: {
        title: 'Téléchargement instantané',
        description: 'Obtenez vos photos instantanément sans filigrane',
      },
      smartSearch: {
        title: 'Recherche intelligente',
        description: 'Ce processus garantit votre confidentialité et empêche d\'autres personnes d\'accéder ou d\'acheter des photos qui ne leur appartiennent pas.',
      },
      cta: 'Trouver vos photos',
    },
    // About Me Page - ABOUT ME
    aboutMe: {
      label: 'À propos',
      title: 'Capturer vos plus beaux',
      titleHighlight: 'moments dans l\'eau',
      intro: 'Je suis <strong>Germán Abreu</strong>, photographe spécialisé dans le surf et les sports nautiques à Lanzarote. Je vis à La Santa et travaille principalement à Famara et dans d\'autres spots de surf de l\'île.',
      paragraph1: 'Après des années à photographier des surfeurs et des élèves d\'écoles de surf, j\'ai toujours constaté le même problème : retrouver ses photos parmi des centaines d\'images était lent et peu pratique.',
      paragraph2: 'C\'est ainsi qu\'est né <strong className="brand-name">Surf Photo Lanzarote</strong>, un moyen simple, rapide et sécurisé pour que chacun retrouve uniquement ses photos, sans recherches interminables et en respectant toujours la vie privée.',
      mission: 'Mon objectif est que vous repartiez avec un souvenir authentique de votre expérience dans l\'eau. Car les vagues passent… mais les souvenirs restent.',
      stats: {
        experience: '+8 ans',
        experienceLabel: 'Expérience',
        schools: '+7',
        schoolsLabel: 'Écoles partenaires',
      },
    },
    // Pricing Page
    pricing: {
      title: 'Tarifs',
      subtitle: 'Choisissez l\'option qui vous convient le mieux',
      plan1: {
        title: 'Votre plus beau souvenir',
        description: 'Une image individuelle en haute qualité',
        details: [
          'Téléchargement instantané',
          'Haute résolution',
          'Format JPG',
          'Adapté à l\'impression',
          'Sans filigrane',
        ],
      },
      plan2: {
        title: 'Tous les souvenirs de votre session',
        description: 'Toutes les photos d\'une même personne le même jour.',
        details: [
          'Toutes les photos du jour',
          'Téléchargement instantané',
          'Haute résolution',
          'Format JPG',
          'Sans filigrane',
        ],
      },
      plan3: {
        title: 'Jour supplémentaire',
        description: 'Ajoutez un autre jour à votre pack.',
        details: [
          'Toutes les photos du jour supplémentaire',
          'Téléchargement instantané',
          'Haute résolution',
          'Sans filigrane',
        ],
      },
      plan4: {
        title: 'Personne supplémentaire',
        description: 'Ajoutez les photos d\'une autre personne. Elles peuvent être du même jour ou d\'un jour différent.',
        details: [
          'Toutes les photos d\'une autre personne',
          'Téléchargement instantané',
          'Haute résolution',
          'Sans filigrane',
        ],
      },
      includes: 'Inclut',
      note: 'Tous les prix incluent le téléchargement instantané sans filigrane',
    },
    // FAQ Page
    faqs: {
      title: 'Questions fréquentes',
      subtitle: 'Trouvez des réponses aux questions les plus courantes sur le fonctionnement de Surf Photo Lanzarote',
      contactText: 'Vous ne trouvez pas la réponse que vous cherchez ?',
      contactLink: 'Contactez-nous',
      questions: [
        {
          q: 'Comment trouver mes photos ?',
          a: 'Sélectionnez votre école de surf, le jour de votre session et téléversez un selfie. Le site vous montrera uniquement les photos où vous apparaissez ce jour-là.',
        },
        {
          q: 'Pourquoi dois-je téléverser un selfie ?',
          a: 'Nous utilisons une technologie de reconnaissance faciale pour identifier votre visage et vous montrer uniquement vos photos, sans avoir à parcourir des centaines d\'images.',
        },
        {
          q: 'Que devient le selfie que je téléverse ?',
          a: 'Le selfie est utilisé uniquement pour effectuer la recherche de ce jour et n\'est ni stocké ni utilisé à d\'autres fins.',
        },
        {
          q: 'Puis-je rechercher des photos de plusieurs jours avec un seul selfie ?',
          a: 'Non. Chaque recherche se fait par jour. Si vous avez surfé plusieurs jours, vous devez téléverser un selfie pour chaque session afin de trouver vos photos.',
        },
        {
          q: 'Que faire si nous sommes deux personnes ou plus ?',
          a: 'Chaque personne doit téléverser son propre selfie pour trouver ses photos. Vous pouvez ajouter les photos de chaque personne au panier et continuer sans problème.',
        },
        {
          q: 'Pourquoi les photos ont-elles un filigrane ?',
          a: 'Les photos sont affichées avec un filigrane pour protéger le travail du photographe. Après l\'achat, vous pourrez les télécharger sans filigrane.',
        },
        {
          q: 'Quelle est la qualité des photos téléchargées ?',
          a: 'Les photos sont téléchargées en haute qualité, prêtes pour un usage personnel, les réseaux sociaux et l\'impression.',
        },
        {
          q: 'Combien de temps mes photos seront-elles disponibles ?',
          a: 'Les photos restent en ligne pendant une durée limitée. Nous vous recommandons de les télécharger dès que possible après l\'achat.',
        },
        {
          q: 'Puis-je acheter des photos de plus d\'un jour ?',
          a: 'Oui. Vous pouvez accéder à différents jours et acheter les photos de chaque session indépendamment.',
        },
        {
          q: 'Les photos sont-elles publiques ?',
          a: 'Non. Les photos sont organisées par école et par jour afin que chaque client n\'accède qu\'à sa propre session.',
        },
        {
          q: 'Le paiement est-il sécurisé ?',
          a: 'Oui. Tous les paiements sont traités via Stripe, une plateforme de paiement sécurisée et fiable.',
        },
        {
          q: 'Que faire si je ne me trouve pas ou si j\'ai un problème ?',
          a: 'Si vous n\'apparaissez pas dans les photos ou si vous rencontrez un problème, contactez-nous et nous vous aiderons.',
        },
      ],
    },
    // School Days Page
    schoolDays: {
      title: 'Sélectionnez un jour',
      subtitle: 'Choisissez le jour de votre session de surf',
      noPhotos: 'Aucune photo disponible pour cette école pour le moment.',
      back: 'Retour',
      searchByDate: 'Rechercher par date',
      search: 'Rechercher',
      availableDays: 'Jours disponibles',
      selectDaySubtitle: 'Sélectionnez un jour pour trouver vos photos',
      loadingDays: 'Chargement des jours disponibles...',
      noDaysYet: 'Aucun jour disponible pour cette école pour le moment.',
      noDaysDesc: 'Les photos sont organisées par jour. Revenez bientôt pour découvrir de nouvelles sessions.',
      photos: 'photos',
      errorLoadingDays: 'Erreur lors du chargement des jours disponibles',
      noPhotosForDate: 'Aucune photo disponible pour cette date',
    },
    // Day Photos Page
    dayPhotos: {
      title: 'Vos photos de surf',
      subtitle: 'Photos du',
      findTitle: 'Trouvez vos photos',
      findSubtitle: 'Choisissez de téléverser un selfie ou d’activer la caméra pour détecter votre visage.',
      clickToSelect: 'Cliquez pour sélectionner un selfie',
      orDragImage: 'ou glissez une image ici',
      faceGuide: {
        hintStarting: 'Préparation du guidage facial…',
        hintNoFace: 'Placez votre visage dans l’ovale',
        hintMultiple: 'Une seule personne à la caméra, s’il vous plaît',
        hintTooFar: 'Approchez-vous un peu',
        hintTooClose: 'Reculez un peu',
        hintMoveLeft: 'Décalez-vous un peu vers la gauche',
        hintMoveRight: 'Décalez-vous un peu vers la droite',
        hintMoveUp: 'Levez un peu le visage',
        hintMoveDown: 'Baissez un peu le visage',
        hintLookStraight: 'Regardez la caméra bien en face',
        turnLeft: 'Tournez la tête vers la gauche et tenez',
        turnRight: 'Maintenant tournez la tête vers la droite',
        circleHead: 'Faites un cercle lent avec la tête',
        coverage: 'Visage {{pct}} %',
        hintHoldStill: 'Parfait, ne bougez plus…',
        hintReady: 'Capturé',
        cameraDenied: 'L’accès à la caméra a été refusé. Vous pouvez téléverser un selfie ou réessayer.',
        choosePrompt: 'Comment voulez-vous que nous trouvions votre visage ?',
        uploadSelfie: 'Téléverser un selfie',
        uploadSelfieHint: 'Utilisez une photo que vous avez déjà',
        startCamera: 'Activer la caméra',
        startCameraHint: 'Nous vous guidons pour détecter votre visage',
        backToChoice: 'Choisir une autre option',
        capture: 'Capturer',
        useGallery: 'Téléverser depuis la galerie',
        loadingDetector: 'Activation de la caméra…',
        galleryMultipleFaces: 'Téléversez un selfie avec une seule personne (pas de photo de groupe).',
        galleryNoFace: 'Aucun visage détecté. Essayez un autre selfie.',
      },
      changePhoto: 'Changer la photo',
      analyzing: 'Analyse en cours...',
      searchPhotos: 'Trouver mes photos',
      errorReadFile: 'Erreur lors de la lecture du fichier. Veuillez essayer une autre image.',
      errorSelectFirst: 'Veuillez d\'abord sélectionner une photo',
      errorEmptyFile: 'Le fichier sélectionné est vide. Veuillez sélectionner une autre image.',
      errorAnalyze: 'Erreur lors de l\'analyse de la photo. Vérifiez que le serveur est en cours d\'exécution.',
      yourSelfie: 'Votre selfie',
      analyzingFace: 'Analyse de votre visage...',
      streamingMatchesSoFar: '{{count}} correspondances trouvées jusqu\'ici — recherche en cours…',
      slowPathHint:
        'ce dossier n\'est pas encore indexé, nous comparons donc photo par photo (lent uniquement la première fois).',
      autoIndexHint:
        'Création de l\'index en arrière-plan — votre prochaine recherche dans ce dossier sera beaucoup plus rapide.',
      indexClustersHint:
        'Création de l\'index facial (clusters)… cela se produit une fois par école/jour — les recherches suivantes n\'utilisent que les clusters.',
      searchDiagnostics:
        'Recherche faciale : {{path}} · mode {{mode}} · {{seconds}}s',
      searchingIn: 'Recherche de correspondances dans',
      analysisResults: 'Résultats de l\'analyse',
      matches: 'Correspondances',
      photosAnalyzed: 'Photos analysées',
      percentage: 'Pourcentage',
      photosFound: 'Photos trouvées',
      addAllMemories: 'Ajouter tous vos souvenirs',
      addAllMemoriesFor35: 'Ajoutez tous vos souvenirs pour {{price}}€',
      similarity: 'Similarité',
      buy: 'Acheter',
      viewLarge: 'Voir en grand',
      noMatch: 'Sans correspondance',
      moreItems: 'de plus',
      uploadPhoto: 'Téléversez votre photo',
      uploadDescription: 'Téléversez une photo nette de votre visage pour trouver vos photos de surf',
      selectFile: 'Sélectionner une photo',
      findPhotos: 'Trouver mes photos',
      results: 'photos trouvées',
      addToCart: 'Ajouter au panier',
      noResults: 'Aucune photo trouvée. Essayez de téléverser une autre photo.',
      savedSearchBanner: 'Affichage de votre recherche enregistrée — toutes les photos correspondantes de votre compte.',
      newSearchLink: 'Lancer une nouvelle recherche avec un autre selfie',
      savedSearchLoading: 'Chargement de la recherche enregistrée...',
      savedSearchNotFound: 'Recherche enregistrée introuvable. Vous pouvez lancer une nouvelle recherche ci-dessous.',
      partnerOffer: 'Offre',
      partnerOfferCta: 'Voir l\'offre',
      partnerOffersWhileSearching: 'Offres partenaires pendant la recherche de vos photos',
    },
    // Checkout Success Page
    account: {
      loginTitle: 'Connexion',
      registerTitle: 'Créez votre profil',
      loginLead: 'Accédez à vos achats et recherches enregistrées pendant jusqu\'à 90 jours.',
      continueGoogle: 'Continuer avec Google',
      orEmail: 'ou avec email',
      displayName: 'Nom',
      email: 'Email',
      password: 'Mot de passe',
      passwordStrengthLabel: 'Force du mot de passe',
      strengthWeak: 'Faible',
      strengthFair: 'Moyen',
      strengthGood: 'Bon',
      strengthStrong: 'Fort',
      strengthHint: 'Utilisez des majuscules, des chiffres et des symboles',
      signIn: 'Se connecter',
      signUp: 'Créer un compte',
      forgotPassword: 'Mot de passe oublié ?',
      forgotPasswordTitle: 'Réinitialiser votre mot de passe',
      forgotPasswordLead: 'Entrez votre email et nous vous enverrons un lien pour choisir un nouveau mot de passe.',
      sendResetLink: 'Envoyer le lien',
      resetLinkSent: 'Si un compte existe avec cet email, nous avons envoyé un lien de réinitialisation.',
      resetLinkError: 'Impossible d\'envoyer le lien. Réessayez.',
      backToSignIn: 'Retour à la connexion',
      resetPasswordTitle: 'Choisir un nouveau mot de passe',
      resetPasswordLead: 'Entrez un nouveau mot de passe pour votre compte.',
      newPassword: 'Nouveau mot de passe',
      savePassword: 'Enregistrer le mot de passe',
      passwordResetSuccess: 'Mot de passe mis à jour. Vous pouvez vous connecter.',
      passwordResetInvalid: 'Ce lien de réinitialisation a expiré ou n\'est pas valide.',
      passwordResetError: 'Impossible de mettre à jour le mot de passe. Réessayez.',
      passwordMismatch: 'Les mots de passe ne correspondent pas.',
      confirmPassword: 'Confirmer le mot de passe',
      noAccount: 'Vous n\'avez pas de compte ?',
      haveAccount: 'Vous avez déjà un compte ?',
      createAccount: 'En créer un',
      photographerCta: 'Vous êtes photographe ?',
      photographerRegister: 'Créer un compte photographe',
      photographerPanel: 'Espace photographe',
      verifyEmailSent: 'Consultez votre email pour vérifier votre compte.',
      backHome: 'Retour à l\'accueil',
      oauthLoading: 'Connexion en cours...',
      oauthError: 'Échec de la connexion. Réessayez.',
      oauthMissing: 'Lien de connexion invalide.',
      emailVerifiedTitle: 'Email vérifié',
      emailVerifiedBody: 'Votre profil est actif. Vous pouvez vous connecter et accéder à votre galerie.',
      goToAccount: 'Aller à mon compte',
      emailInvalidTitle: 'Lien invalide',
      emailInvalidBody: 'Le lien de vérification a expiré ou n\'est pas valide.',
      checkEmailTitle: 'Consultez votre email',
      checkEmailBody: 'Nous vous avons envoyé un lien pour vérifier votre compte.',
      checkEmailSpam: 'Si vous ne le voyez pas, vérifiez les spams ou les promotions.',
      resendVerification: 'Renvoyer l\'email de vérification',
      resendSuccess: 'Si le compte existe, nous avons envoyé un nouvel email.',
      resendError: 'Impossible de renvoyer l\'email. Réessayez.',
      resendEmailRequired: 'Entrez votre email pour renvoyer.',
      emailNotConfigured:
        'Impossible d\'envoyer l\'email depuis le serveur. Contactez le support ou essayez la connexion avec Google.',
      registerCreatedNoEmailTitle: 'Compte créé',
      registerCreatedNoEmailBody:
        'Votre compte a été enregistré, mais nous n\'avons pas pu envoyer l\'email de vérification. Utilisez le renvoi ci-dessous ou connectez-vous avec Google.',
      galleryTitle: 'Ma galerie',
      profileSubtitle: 'Vos achats et recherches enregistrées, jusqu\'à 90 jours après chaque session.',
      tabProfile: 'Profil',
      hello: 'Bonjour',
      signOut: 'Se déconnecter',
      tabPurchases: 'Achats',
      tabSearches: 'Recherches enregistrées',
      statPurchases: 'Achats',
      statSearches: 'Recherches',
      statPhotos: 'Photos',
      editProfile: 'Modifier le profil',
      saveProfile: 'Enregistrer les modifications',
      profileSaved: 'Profil mis à jour',
      profileSaveError: 'Impossible d\'enregistrer le profil',
      changePhoto: 'Changer la photo',
      removePhoto: 'Supprimer la photo',
      photoHint: 'JPG ou PNG, max. 5 Mo. Utilisé comme photo de profil.',
      photoUploadError: 'Impossible de téléverser la photo',
      photoRemoved: 'Photo supprimée',
      photoUploaded: 'Photo mise à jour',
      emailReadonly: 'L\'email ne peut pas être modifié ici',
      authGoogle: 'Compte Google',
      authEmail: 'Email et mot de passe',
      verified: 'Vérifié',
      photosInOrder: 'photos',
      orderDate: 'Date de commande',
      downloadAvailable: 'Téléchargement disponible',
      loadError: 'Impossible de charger votre galerie.',
      emailVerifyRequired:
        'Vérifiez votre email pour accéder à votre galerie, vos achats et vos téléchargements.',
      verifyEmailLink: 'Vérifier l\'email',
      noPurchases: 'Vous n\'avez pas encore d\'achats.',
      noSearches: 'Aucune recherche enregistrée.',
      order: 'Commande',
      downloadZip: 'Télécharger le ZIP',
      expired: 'Période de téléchargement terminée',
      newSearch: 'Trouver des photos d\'un autre jour',
      photosFound: 'photos trouvées',
      viewSession: 'Voir la session',
      searchSavedAt: 'Recherche enregistrée',
      loading: 'Chargement...',
    },
    profilePrompt: {
      beforePayTitle: 'Créez votre profil',
      beforePayBody:
        'Connectez-vous avec Google ou par email pour télécharger vos photos à tout moment pendant jusqu\'à 90 jours après la date de la session.',
      afterPayTitle: 'Enregistrez vos photos dans votre profil',
      afterPayBody:
        'Sans profil, vous ne pouvez télécharger qu\'une seule fois maintenant. Avec un profil, vous y accédez à tout moment pendant 90 jours.',
      exitTitle: 'Enregistrez vos résultats',
      exitBody: 'Créez un profil pour ne pas avoir à retéléverser votre selfie la prochaine fois.',
      createProfile: 'Créer un profil',
      saveToProfile: 'Enregistrer dans mon profil',
      saveResults: 'Enregistrer les résultats',
      continueGoogle: 'Continuer avec Google',
      notNow: 'Pas maintenant',
      downloadNowOnly: 'Télécharger uniquement maintenant',
    },
    checkoutSuccess: {
      loading: 'Chargement de vos téléchargements...',
      paymentNotFound: 'Paiement introuvable',
      noDownloadData: 'Aucune donnée de téléchargement.',
      testHint: 'Pour tester la section téléchargements sans payer, visitez :',
      backHome: 'Retour à l\'accueil',
      title: 'Paiement effectué !',
      subtitle: 'Merci pour votre achat. Voici vos photos à télécharger sans filigrane.',
      hello: 'Bonjour',
      downloads: 'Téléchargements',
      downloadsIntro: 'Vous pouvez télécharger chaque photo en haute qualité en cliquant sur le bouton ou sur l\'image.',
      downloadAll: 'Télécharger toutes les photos',
      downloadingAll: 'Démarrage du téléchargement…',
      downloadThis: 'Télécharger cette photo',
      downloadingOne: 'Téléchargement…',
      downloadTitle: 'Télécharger',
      total: 'Total',
      photo: 'photo',
      photos: 'photos',
      exploreMore: 'Voir plus d\'écoles',
      missingSession: 'session_id manquant ou mode test (test=1).',
      paymentIncomplete:
        'Nous n\'avons pas pu charger les détails de votre paiement pour le moment, mais si vous avez finalisé le paiement, il a bien été effectué — veuillez ne pas payer à nouveau. Actualisez cette page dans un instant, ou contactez-nous avec votre email et nous vous enverrons les photos.',
      loadError: 'Impossible de charger les détails du paiement.',
      unexpectedResponse: 'Réponse inattendue du serveur.',
      connectionError: 'Erreur de connexion. Vérifiez que le backend est en cours d\'exécution.',
      downloadsOnThisPage:
        'Vos photos sont sur cette page. Utilisez « Télécharger toutes les photos » ou chaque bouton « Télécharger cette photo » ci-dessous.',
      guestProfileBenefit:
        'Vous pouvez télécharger vos photos ici à tout moment avec ce lien. Créez un profil pour les enregistrer dans votre galerie et y accéder quand vous le souhaitez.',
      createProfileCta: 'Créer un profil',
      goToGallery: 'Ma galerie',
      downloadFailed: 'Échec du téléchargement. Essayez « Télécharger toutes les photos » ou actualisez cette page.',
      voucherEmail:
        'Nous avons également envoyé par email votre confirmation d\'achat avec les bons partenaires en PDF.',
    },
    checkoutCancel: {
      title: 'Paiement annulé',
      message: 'Aucun prélèvement n\'a été effectué. Vous pouvez retourner au panier ou continuer à naviguer.',
      backHome: 'Retour à l\'accueil',
      viewSchools: 'Voir les écoles',
    },
    // Cart Modal
    cart: {
      title: 'Vos souvenirs',
      item: 'article',
      items: 'articles',
      empty: 'Votre panier est vide',
      emptyDesc: 'Explorez nos photos et ajoutez vos souvenirs préférés',
      explorePhotos: 'Explorer les photos',
      total: 'Total',
      checkout: 'Passer au paiement',
      remove: 'Supprimer',
      beforePaymentDownloadNotice:
        'Vos photos seront disponibles au téléchargement sur cette même page immédiatement après le paiement',
      emailPlaceholder: 'Votre email',
      namePlaceholder: 'Votre nom',
      subtotal: 'Sous-total',
      igic: 'IGIC ({rate}%)',
      taxesNotIncludedNote: 'Prix hors taxes',
      discount: 'Remise',
      processing: 'Traitement...',
      proceedToPayment: 'Procéder au paiement',
      continueShopping: 'Continuer vos achats',
      securePayment: 'Paiement sécurisé',
      instantDownload: 'Téléchargement instantané',
      closeCart: 'Fermer le panier',
      removeItem: 'Supprimer l\'article',
      packComplete: 'Pack complet',
      photo: 'photo',
      photos: 'photos',
      digitalFormat: 'Format numérique',
      highResolution: 'Haute résolution',
      errorEmailNameRequired: 'Entrez votre email et votre nom pour continuer',
      errorInvalidEmail: 'Entrez un email valide (ex. : vous@email.com)',
      errorEmptyCart: 'Votre panier est vide',
      errorCheckoutSession: 'Impossible de créer la session de paiement',
      errorNoPaymentUrl: 'URL de paiement non reçue. Vérifiez que Stripe est configuré.',
      errorPaymentFailed: 'Erreur lors du traitement du paiement',
    },
    // Sticky purchase flow pricing bar
    purchasePricingBar: {
      ariaLabel: 'Tarifs',
      disclaimer:
        'La remise sur le 2e pack s\'applique lorsque les deux packs sont ajoutés au panier ensemble. Prix hors taxes (IGIC).',
      blocks: [
        {
          description: 'Votre meilleure photo',
          badge: '1 photo',
        },
        {
          description: 'Toutes vos photos d\'une session',
          badge: 'Pack complet',
        },
        {
          description: 'Session supplémentaire pour vous',
          badge: '2e pack',
        },
        {
          description: 'Toutes les photos d\'un ami ou d\'un proche',
          badge: '2e pack',
        },
      ],
    },
    // Partners
    partners: {
      title: 'Écoles partenaires',
    },
    // Footer
    footer: {
      brand: 'Surf Photo Lanzarote',
      description: 'Marketplace premium de photographie de surf à Lanzarote',
      navigation: 'Navigation',
      home: 'Accueil',
      schools: 'Écoles',
      howItWorks: 'Comment ça marche',
      pricing: 'Tarifs',
      legal: 'Mentions légales',
      legalNotice: 'Mentions légales',
      privacy: 'Politique de confidentialité',
      terms: 'Conditions générales',
      contact: 'Contact',
      email: 'info@surfphotolanzarote.com',
      location: 'Lanzarote, Îles Canaries',
      rights: 'Tous droits réservés',
    },
    // Common
    common: {
      loading: 'Chargement...',
      error: 'Une erreur s\'est produite',
      close: 'Fermer',
      cancel: 'Annuler',
      confirm: 'Confirmer',
      save: 'Enregistrer',
      delete: 'Supprimer',
      edit: 'Modifier',
      back: 'Retour',
    },
  };
